<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div class="device-icon-pin" :class="sizeClass" :title="device.deviceName">
      <!-- The tank itself inside the ring, the ring carrying its level. -->
      <ControlStatusRing :value="level" :accent="accent">
        <div class="tank-pin-art">
          <TankAnimation :kind="tankKind" :level="level" :alarm="alarm" :active="blowerOn" />
        </div>
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="alarm"
      :accent="accent"
      class="tank-card-face"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <img v-if="thumbUrl" :src="thumbUrl" alt="" class="tank-card-face__img" draggable="false" />
        <!-- A tank with its own 3D model keeps it: the water line in the model is
             the live level, and the typed drawing is the fallback below. -->
        <img
          v-else-if="modelPreviewUrl"
          :src="modelPreviewUrl"
          alt=""
          class="tank-card-face__img tank-card-face__img--model"
          draggable="false" />
        <TankAnimation
          v-else
          class="tank-card-face__figure"
          :kind="tankKind"
          :level="level"
          :alarm="alarm"
          :active="blowerOn" />
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="tank-card-face__value" :class="{ 'tank-card-face__value--alarm': alarm }">{{ headline }}</span>
      </template>

      <template v-if="cardFacts.length" #facts>
        <span v-for="fact in cardFacts" :key="fact" class="tank-card-face__fact">{{ fact }}</span>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'water_full'"
      :name="device.deviceName"
      :is-on="false"
      active-color="cyan-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <div class="tank-sheet" :style="{ '--sheet-accent': accent }">
      <!-- HERO — the tank itself, filled to the level it reports. -->
      <div class="tank-hero">
        <div class="tank-hero-figure" :class="{ 'tank-hero-figure--alarm': alarm }">
          <TankAnimation :kind="tankKind" :level="level" :alarm="alarm" :active="blowerOn" />
        </div>
        <div class="tank-hero-value" :class="{ 'tank-value--alarm': alarm }">{{ levelText }}</div>
        <div class="tank-hero-caption">{{ alarm ? alarmLabel : tankLabel }}</div>
      </div>

      <!-- WHAT IT HOLDS — the readings beside the level, one quiet row each. -->
      <div v-if="sheetRows.length" class="tank-rows">
        <div v-for="row in sheetRows" :key="row.key" class="tank-row">
          <span class="tank-row-key">{{ row.label }}</span>
          <span class="tank-row-value">{{ row.value }}</span>
        </div>
      </div>

      <!-- ITS OWN CONTROLS — a cistern's refill request, a plant's blower. -->
      <ControlToggleRow
        v-if="hasRefill"
        :model-value="refillOn"
        :label="tt('Refill request', 'Zahteva za polnjenje')"
        icon="drop"
        :disabled="controlling"
        @update:model-value="setRefill" />
      <ControlToggleRow
        v-if="hasBlower"
        :model-value="blowerOn"
        :label="tt('Aeration blower', 'Črpalka za zrak')"
        icon="wind"
        :disabled="controlling"
        @update:model-value="setBlower" />

      <!-- HISTORY — the level is a logged analog reading; keep its log door. -->
      <div v-if="loggableValues.length" class="tank-history">
        <p class="tank-history-title">
          <ControlIcon name="activity" size="1em" />
          {{ tt('History', 'Zgodovina') }}
        </p>
        <div class="tank-history-rows">
          <ValueLogButton
            v-for="v in loggableValues"
            :key="v.id"
            :device-value="v"
            :device="{ id: device.id, deviceName: device.deviceName }" />
        </div>
      </div>

      <p v-if="lastUpdated" class="tank-updated">{{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}</p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import { tankKindOf, type TankKind } from './deviceWidgetTypes';
import { TANK_VARIANTS } from '../device-editor/variants/tank';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import { useDeviceModelThumbnail } from '../../composables/useDeviceModelThumbnail';
import DeviceCardShell from './DeviceCardShell.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import TankAnimation from './tank/TankAnimation.vue';
import ValueLogButton from './ValueLogButton.vue';
import { tt } from '../../utils/consoleText';
import { parseDeviceModel, type DeviceModel3d } from '@/utils/bimDeviceModel';
import { getTankLevelPct } from '@/utils/waterTankDevices';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (values) => {
    if (values?.length) initializeValues(values);
  },
  { deep: true },
);

/** The live reading, then the device row — the same fold the 3D tank reads. */
function rowValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return (props.device.deviceValues ?? []).find((value) => value.valueKey === key)?.currentValue;
}

function numeric(keys: string[]): number | null {
  for (const key of keys) {
    const raw = rowValue(key);
    const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function truthy(keys: string[]): boolean {
  return keys.some((key) => {
    const raw = rowValue(key);
    return raw === true || raw === 1 || raw === 'on' || raw === 'true';
  });
}

function hasReading(key: string): boolean {
  return (props.device.deviceValues ?? []).some((value) => value.valueKey === key);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
}

// ── The tank ─────────────────────────────────────────────────────────────
/** The level, from any of the keys the hub and KNX imports spell it with. */
const level = computed(() => getTankLevelPct(props.device, currentValues.value));

/** Which vessel this tank is — the editor's pick, the class, then the name. */
const tankKind = computed<TankKind>(() =>
  tankKindOf({
    icon: props.device.icon,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    settings: props.device.settings,
  }),
);

const tankLabel = computed(() => {
  const meta = TANK_VARIANTS.options.find((entry) => entry.value === tankKind.value);
  return meta ? tt(meta.label[0], meta.label[1]) : '';
});

const volume = computed(() => numeric(['volume']));
const temperature = computed(() => numeric(['temperature']));

const lowLevelAlarm = computed(() => truthy(['low_level_alarm']));
const faultAlarm = computed(() => truthy(['alarm']));
const alarm = computed(() => lowLevelAlarm.value || faultAlarm.value);
const alarmLabel = computed(() => (lowLevelAlarm.value ? tt('Low level', 'Nizka gladina') : tt('Fault', 'Napaka')));

const hasRefill = computed(() => hasReading('refill_request'));
const refillOn = computed(() => truthy(['refill_request']));
const hasBlower = computed(() => hasReading('air_blower'));
const blowerOn = computed(() => truthy(['air_blower']));

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

const loggableValues = computed(() =>
  (props.device.deviceValues ?? []).filter((value) => value.loggingType && value.loggingType !== 'None'),
);

const thumbUrl = computed(() => getDefaultThumbnailUrl(props.device));

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** Water's own colour — the sheet, the ring and the card's edge share it. */
const accent = computed(() => (alarm.value ? 'rgb(239, 68, 68)' : 'rgb(6, 182, 212)'));

/**
 * The device's own 3D model (`settings.model3d`), drawn at its live level — the
 * same preference the sensor tile has always given a modelled tank.
 */
const deviceModel = computed<DeviceModel3d | null>(() => {
  const settings = (props.device as { settings?: unknown }).settings;
  return settings ? parseDeviceModel(settings) : null;
});

const modelLevel = computed(() => {
  const type = deviceModel.value?.library?.type;
  if (type !== 'water-tank' && type !== 'waste-tank') return null;
  return level.value;
});

const modelPreviewUrl = useDeviceModelThumbnail(deviceModel, modelLevel);

const levelText = computed(() => (level.value === null ? '—' : `${Math.round(level.value)} %`));
const volumeText = computed(() => (volume.value === null ? null : `${formatNumber(volume.value)} m³`));
const temperatureText = computed(() => (temperature.value === null ? null : `${formatNumber(temperature.value)} °C`));

const headline = computed(() => {
  if (level.value === null) return volumeText.value ?? '—';
  return volumeText.value ? `${levelText.value} · ${volumeText.value}` : levelText.value;
});

const cardFacts = computed(() =>
  [volumeText.value, temperatureText.value, alarm.value ? alarmLabel.value : null].filter(
    (fact): fact is string => fact !== null,
  ),
);

const sheetRows = computed(() => {
  const rows: Array<{ key: string; label: string; value: string }> = [];
  if (volumeText.value) rows.push({ key: 'volume', label: tt('Volume', 'Prostornina'), value: volumeText.value });
  if (temperatureText.value) {
    rows.push({ key: 'temperature', label: tt('Temperature', 'Temperatura'), value: temperatureText.value });
  }
  return rows;
});

// ── Actions ──────────────────────────────────────────────────────────────
async function setRefill(next: boolean) {
  await setProperty(props.device, 'refill_request', next);
}

async function setBlower(next: boolean) {
  await setProperty(props.device, 'air_blower', next);
}
</script>

<style scoped>
/* The tank inside the pin: sized off the ring's clear circle, like the fan's
   own drawn icon, so every pin size (sm/md/lg/fluid) scales with it. */
.tank-pin-art {
  width: 64%;
  height: 64%;
}

/* ── Card face ─────────────────────────────────────────────────────────────── */
.tank-card-face__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.tank-card-face__img--model {
  padding: 2px;
  box-sizing: border-box;
}

.tank-card-face__figure {
  width: 100%;
  height: 100%;
}

.tank-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  color: #0e7490;
}

.dark .tank-card-face__value {
  color: #67e8f9;
}

.tank-card-face__fact + .tank-card-face__fact::before {
  content: '·';
  margin: 0 0.3em;
  color: rgb(148, 163, 184);
}

.tank-card-face__fact {
  font-size: var(--card-fact, 0.7rem);
  color: #64748b;
  white-space: nowrap;
}

.dark .tank-card-face__fact {
  color: #94a3b8;
}

.tank-card-face__value--alarm,
.tank-value--alarm {
  color: #dc2626 !important;
}

.dark .tank-card-face__value--alarm,
.dark .tank-value--alarm {
  color: #f87171 !important;
}

/* The model inside the compact tile's icon circle */
.tank-tile__model {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 1px;
  box-sizing: border-box;
}

/* ── Control mode: the sheet body ─────────────────────────────────────────── */
.tank-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

.tank-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding-top: 0.25rem;
}

.tank-hero-figure {
  width: 7.5rem;
  height: 7.5rem;
  color: rgb(148, 163, 184);
  transition: color 0.3s ease;
}

.dark .tank-hero-figure {
  color: rgb(100, 116, 139);
}

.tank-hero-figure--alarm {
  color: rgb(248, 113, 113);
}

.tank-hero-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  color: rgb(var(--gray-800));
}

.dark .tank-hero-value {
  color: rgb(var(--gray-100));
}

.tank-hero-caption {
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
  text-align: center;
}

/* WHAT IT HOLDS — one row per reading */
.tank-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(148, 163, 184, 0.22);
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
}

.tank-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}

.tank-row-key {
  font-size: 0.8125rem;
  color: rgb(var(--gray-500));
}

.tank-row-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgb(var(--gray-800));
}

.dark .tank-row-value {
  color: rgb(var(--gray-100));
}

/* HISTORY */
.tank-history {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tank-history-title {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.tank-history-rows {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tank-updated {
  margin: 0;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
  text-align: center;
}
</style>
